import { useEffect, useState, useCallback } from "react";
import { api, mediaUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle2, Pencil, X, Save, Loader2 } from "lucide-react";

const TYPES = ["Outerwear", "Dresses", "Tops", "Bottoms", "Tailoring", "Footwear", "Bags", "Accessories", "Knitwear", "Uncategorized"];
const STATUS = ["", "draft", "published", "hidden"];

function EditModal({ product, onClose, onSaved }) {
  const [p, setP] = useState({ ...product, style_labels: (product.style_labels || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/products/${p.id}`, {
        title: p.title,
        product_type: p.product_type,
        style_labels: p.style_labels.split(",").map((s) => s.trim()).filter(Boolean),
        color: p.color,
        fabric: p.fabric,
        occasion: p.occasion,
        shop_url: p.shop_url,
      });
      toast.success("Product updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const F = (label, key) => (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#8E8E9A]">{label}</label>
      {key === "product_type" ? (
        <select value={p[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`modal-${key}`} className="w-full mt-1 bg-[#0B0B0C] border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-[#D4AF37] outline-none">
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      ) : (
        <input value={p[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`modal-${key}`} className="w-full mt-1 bg-[#0B0B0C] border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-[#141417] border border-white/10 rounded-sm max-w-2xl w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="product-edit-modal">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-2xl font-light">Edit Product</h3>
          <button onClick={onClose} data-testid="modal-close"><X size={20} className="text-[#8E8E9A] hover:text-white" /></button>
        </div>
        <div className="flex gap-6">
          <div className="w-32 aspect-[3/4] rounded-sm overflow-hidden bg-[#0B0B0C] shrink-0">
            <img src={mediaUrl(p.hero_thumbnail)} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div className="col-span-2">{F("Title", "title")}</div>
            {F("Type", "product_type")}
            {F("Style (comma)", "style_labels")}
            {F("Color", "color")}
            {F("Fabric", "fabric")}
            {F("Occasion", "occasion")}
            {F("Shop URL", "shop_url")}
          </div>
        </div>
        <div className="flex justify-end mt-8">
          <button onClick={save} disabled={saving} data-testid="modal-save" className="flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-2.5 rounded-sm text-sm font-medium hover:bg-[#C5A059] transition-colors">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const [status, setStatus] = useState("");
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    const params = status ? { status } : {};
    api.get("/admin/products", { params }).then((r) => setProducts(r.data));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    await api.post(`/admin/products/${id}/${action}`);
    toast.success(action === "publish" ? "Published" : "Hidden");
    load();
  };

  return (
    <div className="fade-in">
      <p className="overline mb-2">Master Catalogue</p>
      <h1 className="font-serif text-4xl font-light mb-8">Products</h1>

      <div className="flex gap-2 mb-6">
        {STATUS.map((s) => (
          <button
            key={s || "all"}
            data-testid={`product-filter-${s || "all"}`}
            onClick={() => setStatus(s)}
            className={`text-xs px-4 py-2 rounded-full border transition-all ${status === s ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-[#8E8E9A] hover:text-[#F8F6F0]"}`}
          >
            {s ? s[0].toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>

      <div className="border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#141417] text-[#8E8E9A] font-mono text-[10px] uppercase tracking-[0.15em]">
            <tr>
              <th className="text-left p-4">Product</th>
              <th className="text-left p-4 hidden md:table-cell">Type</th>
              <th className="text-left p-4 hidden lg:table-cell">Attributes</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="products-table">
            {products.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/5 transition-colors" data-testid={`product-row-${p.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-12 rounded-sm overflow-hidden bg-[#0B0B0C] shrink-0">
                      <img src={mediaUrl(p.hero_thumbnail)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="font-serif text-base font-light">{p.title}</span>
                  </div>
                </td>
                <td className="p-4 hidden md:table-cell text-[#8E8E9A]">{p.product_type}</td>
                <td className="p-4 hidden lg:table-cell text-[#8E8E9A] text-xs">{[p.color, p.fabric, p.occasion].filter(Boolean).join(" · ")}</td>
                <td className="p-4"><StatusBadge status={p.status} /></td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`} className="p-2 rounded-sm border border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"><Pencil size={13} /></button>
                    {p.status !== "published" && (
                      <button onClick={() => act(p.id, "publish")} data-testid={`publish-product-${p.id}`} className="p-2 rounded-sm border border-white/10 hover:border-emerald-500 hover:text-emerald-400 transition-colors"><CheckCircle2 size={13} /></button>
                    )}
                    {p.status !== "hidden" ? (
                      <button onClick={() => act(p.id, "hide")} data-testid={`hide-product-${p.id}`} className="p-2 rounded-sm border border-white/10 hover:border-red-500 hover:text-red-400 transition-colors"><EyeOff size={13} /></button>
                    ) : (
                      <button onClick={() => act(p.id, "publish")} data-testid={`unhide-product-${p.id}`} className="p-2 rounded-sm border border-white/10 hover:border-emerald-500 hover:text-emerald-400 transition-colors"><Eye size={13} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && <p className="p-8 text-center text-[#8E8E9A] font-light">No products.</p>}
      </div>

      {editing && <EditModal product={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
